import type * as React from 'react';
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { cn } from '@/shared/lib/utils';
import { Text } from '../../text';
import { FormControlLabel } from './FormControlLabel';

/**
 * FieldItem コンポーネント
 *
 * Figma デザインシステム準拠のフィールドコンポーネント
 * React Hook Form の Controller を内包し、統一されたフィールド UI を提供
 *
 * 機能:
 * - ラベル、必須バッジ、サポートテキスト、エラーメッセージの統合表示
 * - React Hook Form との完全統合
 * - Zod スキーマからの自動必須判定
 * - readOnly モード対応
 * - 任意のフォームコントロール（Input, Textarea, Select）をchildrenとして受け取る
 *
 * 使用例:
 * ```tsx
 * <FieldItem
 *   control={form.control}
 *   name="email"
 *   label="メールアドレス"
 *   required
 * >
 *   {({ field, fieldId, hasError }) => (
 *     <Input
 *       {...field}
 *       id={fieldId}
 *       type="email"
 *       placeholder="email@example.com"
 *       className={cn(hasError && 'border-destructive')}
 *     />
 *   )}
 * </FieldItem>
 * ```
 */
export type FieldItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  /** React Hook Form の control */
  control: Control<TFieldValues>;
  /** フィールド名 */
  name: TName;
  /** ラベルテキスト */
  label: string | null;
  /** 必須フラグ（省略時は Zod スキーマから自動判定） */
  required?: boolean;
  /** サポートテキスト */
  supportText?: string;
  /** サポートテキストを表示するか */
  showSupportText?: boolean;
  /** 読み取り専用モード */
  readOnly?: boolean;
  /** 読み取り専用時の表示値（省略時はフィールドの値を使用） */
  displayValue?: string;
  /** className */
  className?: string;
  /**
   * フォームコントロール（Input, Textarea, Selectなど）をレンダリングする関数
   * field: React Hook Formのfieldオブジェクト
   * fieldId: フィールドのID（label htmlForと紐付け用）
   * hasError: エラー状態
   */
  children: (props: {
    field: ControllerRenderProps<TFieldValues, TName>;
    fieldId: string;
    hasError: boolean;
  }) => React.ReactNode;
};

export const FieldItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  supportText,
  showSupportText = false,
  readOnly = false,
  displayValue,
  className,
  children,
}: FieldItemProps<TFieldValues, TName>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const fieldId = `field-${String(name).replace(/[.[\]]/g, '-')}`;
        const errorMessage = fieldState.error?.message;
        const hasError = !!fieldState.error;

        // 必須判定: Props で指定されていない場合は、エラー情報から判定
        // TODO: より厳密な Zod スキーマからの判定を実装する場合はここを修正
        const isRequired = required ?? false;

        // readOnly モード
        if (readOnly) {
          return (
            <div className={cn('flex flex-col gap-1', className)}>
              <FormControlLabel label={label} showRequired={false} showSupportText={false} />
              <Text className="leading-[22px]">{displayValue || field.value || '-'}</Text>
            </div>
          );
        }

        // 通常モード
        return (
          <div className={cn('flex w-full flex-col items-start gap-2', className)}>
            {/* ラベル + 必須バッジ + サポートテキスト */}
            <FormControlLabel
              label={label}
              showRequired={isRequired}
              showSupportText={showSupportText}
              supportText={supportText}
              htmlFor={fieldId}
            />

            {/* 入力フィールド（children経由） */}
            {children({ field, fieldId, hasError })}

            {/* エラーメッセージ */}
            {errorMessage && (
              <Text id={`${fieldId}-error`} color="error" variant="description">
                {errorMessage}
              </Text>
            )}
          </div>
        );
      }}
    />
  );
};

FieldItem.displayName = 'FieldItem';
